import React, { useEffect, useState } from "react";
import { MyAccount } from "../../../utils/Customer/CustomerData";
import CommonButton from "../../../components/CommonButton";
import { Gift, PawPrint, Edit2, Menu, X, LogOut } from "lucide-react";
import ProfileUI from "../../../components/customer/ProfileUI";
import AddPetProfileUI from "../../../components/customer/AddPetProfileUI";

import { IUserProfileRequest } from "../../../types/Request/CustomerRequest";
import {
  useLazyGetPetProfileQuery,
  useLazyGetUserProfileQuery,
  useUpsertUserProfileMutation,
} from "../../../redux/actions/Customer/profileCustomer";
import { useAlert } from "../../../components/customer/AlertContext";
import { PetData } from "../../../types/Response/CustomerResponse";
import ViewPetProfileUI from "../../../components/customer/ViewPetProfileUI";
import CustomerOrdersScreen from "./CustomerOrdersScreen";
import CustomerApplicationsScreen from "./CustomerApplicationsScreen";
import { useLocation, useNavigate } from "react-router-dom";
import AddressManager from "../../../components/Address/AddressForm";
import WalletScreen from "../../../components/customer/WalletScreen";
import CustomerSubscriptions from "../CustomerCheckout/CustomerSubscriptions";
import { useDispatch } from "react-redux";
import CustomerMyBookingsScreen from "./CustomerMyBookingsScreen";
import { logoutCustomer } from "../../../redux/reducers/authReducer";
import RewardDashboard from "../../../components/customer/RewardDashboard";
import ReferralDashboardView from "../../../components/customer/ReferralDashboardView";

const CustomerProfileScreen: React.FC = () => {
  const [selected, setSelected] = useState("profile");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [selectedPet, setSelectedPet] = useState<PetData | null>(null);
  const [viewingPet, setViewingPet] = useState<PetData | null>(null);

  const [profileData, setProfileData] = useState<
    IUserProfileRequest | undefined
  >(undefined);
  const [petData, setPetData] = useState<PetData[]>([]);
  const [updateProfile] = useUpsertUserProfileMutation();
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const [getUserProfile] = useLazyGetUserProfileQuery();
  const [getAllPets] = useLazyGetPetProfileQuery();
  const { showConfirm } = useAlert();

  const location = useLocation();
  const selectedOrderId = location.state?.orderId;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!location.state) return;

    if (location.state.activeTab) {
      setSelected(location.state.activeTab);
    }

    // Clear the temporary navigation state
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.state, location.pathname, navigate]);

  const fetchUserProfile = async () => {
    try {
      setIsProfileLoading(true);
      const response = await getUserProfile().unwrap();
      console.log("USER PROFILE DETAIL", response);
      setProfileData(response.data);
    } catch (error: any) {
      console.error("Fetch Profile Error:", error);
      showConfirm({
        title: "Network Error",
        message: error?.data?.message || "Something went wrong",
        type: "error",
        confirmText: "OK",
      });
    } finally {
      setIsProfileLoading(false);
    }
  };

  const fetchGetAllPets = async () => {
    try {
      const response = await getAllPets().unwrap();
      setPetData(response?.data || []);
    } catch (error) {
      console.error("Fetch Pets Error:", error);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    fetchGetAllPets();
  }, []);

  const handelSaveProfile = async (profilePayload: IUserProfileRequest) => {
    try {
      if (!profilePayload.firstName?.trim())
        return missingFieldAlert("First name");
      if (!profilePayload.lastName?.trim())
        return missingFieldAlert("Last name");

      const response = await updateProfile(profilePayload).unwrap();

      if (response?.success) {
        showConfirm({
          title: "Profile Updated",
          message: response.message || "Profile updated successfully",
          type: "success",
          confirmText: "Continue",
        });
        localStorage.setItem("userName", profilePayload.firstName);
        fetchUserProfile();
      } else {
        showConfirm({
          title: "Update Failed",
          message: response?.message || "Failed to update profile",
          type: "error",
          confirmText: "OK",
        });
      }
    } catch (error: any) {
      console.error("Profile Save Error:", error);
      showConfirm({
        title: "Network Error",
        message: error?.data?.message || "Something went wrong",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  const missingFieldAlert = (fieldName: string) => {
    return showConfirm({
      title: "Validation Error",
      message: `${fieldName} is required`,
      type: "error",
      confirmText: "OK",
    });
  };

  const handleViewPet = (pet: PetData) => {
    setViewingPet(pet);
    setSelected("viewPetProfile");
    setIsSidebarOpen(false);
  };

  const handleEditPet = (pet: PetData) => {
    setSelectedPet(pet);
    setSelected("addPetProfile");
    setIsSidebarOpen(false);
  };

  const handleAddNewPetClick = () => {
    setSelectedPet(null);
    setSelected("addPetProfile");
    setIsSidebarOpen(false);
  };

  const getHeaderTitle = () => {
    if (selected === "viewPetProfile") return "Pet Profile";
    if (selected === "addPetProfile")
      return selectedPet ? "Edit Pet" : "Add Pet";
    const foundItem = [...MyAccount].find((item) => item.id === selected);
    return foundItem ? foundItem.text : "Dashboard";
  };

  const handleLogout = () => {
    dispatch(logoutCustomer());

    localStorage.clear();
    sessionStorage.clear();

    navigate("/", { replace: true });
  };

  const renderContent = () => {
    switch (selected) {
      case "profile":
        return (
          <ProfileUI
            onSave={(profilePayload) => handelSaveProfile(profilePayload)}
            initialData={profileData}
          />
        );
      case "addresses":
        return <AddressManager />;

      case "viewPetProfile":
        return viewingPet ? (
          <ViewPetProfileUI
            petId={viewingPet.id}
            onBackClick={() => setSelected("profile")}
          />
        ) : null;

      case "addPetProfile":
        return (
          <AddPetProfileUI
            idFromRoute={selectedPet?.id}
            goTo={(screen) => {
              if (screen === "dashboard") {
                fetchGetAllPets();
                setSelected("profile");
                setIsSidebarOpen(false);
              }
            }}
          />
        );

        case "referral":
          return <ReferralDashboardView/>;

      case "rewards":
        return <RewardDashboard/>;
  
      case "myBookings":
        return <CustomerMyBookingsScreen />;
      case "wallet":
        return <WalletScreen />;
      case "Subscription":
        return <CustomerSubscriptions />;
      case "orders":
        return <CustomerOrdersScreen selectedOrderId={selectedOrderId} />;
      case "myApplications":
        return <CustomerApplicationsScreen />;

      case "signout":
        return (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-white rounded-2xl border border-[#EBEBEB] shadow-xs p-10 max-w-xl w-full text-center">
              {/* Logout Icon Container */}
              <div className="w-16 h-16 mx-auto rounded-full bg-[#FFF0F0] flex items-center justify-center mb-6">
                <LogOut className="w-7 h-7 text-[#E53935] translate-x-0.5" />
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-[#234722] mb-3">
                Sign Out
              </h2>

              {/* Message */}
              <p className="text-gray-500 text-base leading-relaxed max-w-sm mx-auto mb-8">
                Are you sure you want to sign out of your account? You can
                always sign back in anytime.
              </p>

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => setSelected("profile")}
                  className="px-8 py-2.5 rounded-lg border border-[#234722] text-[#234722] font-semibold text-sm hover:bg-[#F4F8F3] transition-colors min-w-[130px]"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="px-8 py-2.5 rounded-lg bg-[#D32F2F] text-white font-semibold text-sm hover:bg-[#C62828] transition-colors min-w-[130px] shadow-xs"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (isProfileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F2]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-[#A8D97B] border-t-[#2F5E2E] rounded-full animate-spin" />
          <p className="text-[#2F5E2E] font-medium">Loading Profile...</p>
        </div>
      </div>
    );
  }

  const hasPets = petData && petData.length > 0;

  const SidebarContent = () => (
    <div className="space-y-4 flex flex-col">
      <div className="space-y-4">
        <div className="mt-4 border rounded-xl p-4 bg-[#A8D97B]/20 border-[#A8D97B]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PawPrint className="w-6 h-6 text-[#8B6B3E]" />
              <p className="text-lg font-medium text-[#2F5E2E]">
                {hasPets ? "Your Pets" : "Add Pet Profile"}
              </p>
            </div>
            {hasPets && (
              <button
                onClick={handleAddNewPetClick}
                className="text-xs bg-[#2F5E2E] text-white px-2 py-1 rounded-md hover:bg-[#234722] transition font-semibold"
              >
                + Add New
              </button>
            )}
          </div>

          {!hasPets ? (
            <>
              <p className="text-sm text-gray-600 mt-1">
                Complete pet details & earn rewards
              </p>
              <div className="mt-2 mb-4 inline-flex items-center gap-1 bg-[#6DBE45] text-white text-xs px-3 py-1 rounded-full font-medium">
                <Gift className="w-3 h-3" />
                Get 100 Points
              </div>
              <CommonButton
                text="Add Profile"
                variant="filled"
                rounded="md"
                className="w-full font-semibold"
                onClick={handleAddNewPetClick}
              />
            </>
          ) : (
            <div className="mt-3 space-y-2">
              {petData.map((pet) => (
                <div
                  key={pet.id}
                  onClick={() => handleViewPet(pet)}
                  className={`flex items-center justify-between p-2 rounded-xl border cursor-pointer transition-all ${
                    selected === "viewPetProfile" && viewingPet?.id === pet.id
                      ? "bg-[#A8D97B]/30 border-[#A8D97B]"
                      : "bg-white border-[#DDE7D8] hover:border-[#6DBE45]"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    {pet.photoUrl ? (
                      <img
                        src={pet.photoUrl}
                        alt={pet.petName}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#A8D97B]/40 flex items-center justify-center text-[#2F5E2E] font-bold text-sm">
                        {pet.petName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="leading-tight">
                      <p className="text-sm font-bold text-gray-800">
                        {pet.petName}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {pet.breed || "Unknown Breed"}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPet(pet);
                    }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[#2F5E2E] hover:bg-[#FAFBF8] transition"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl space-y-1">
          <p className="text-xs text-gray-400 px-2 tracking-wider font-bold mb-2">
            MY ACCOUNT
          </p>
          {MyAccount.map((item) => {
            return (
              <div
                key={item.id}
                onClick={() => {
                  setViewingPet(null);
                  setIsSidebarOpen(false);
                  setSelected(item.id);
                }}
                className={`px-3 py-2.5 rounded-xl cursor-pointer text-sm transition-all ${
                  selected === item.id
                    ? "bg-[#A8D97B]/30 text-[#2F5E2E] font-bold"
                    : "hover:bg-[#FAFBF8] text-gray-700"
                } ${
                  item.id === "signout"
                    ? "text-red-500 hover:bg-red-50 font-bold"
                    : ""
                }`}
              >
                {item.text}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row relative overflow-x-hidden">
      <div className="md:hidden w-full bg-white h-16 px-4 border-b border-[#DDE7D8] flex items-center justify-between sticky top-0 z-40 shadow-xs">
        <div className="flex items-center gap-2">
          <PawPrint className="w-6 h-6 text-[#2F5E2E]" />
          <h1 className="text-lg font-bold text-[#2F5E2E]">
            {getHeaderTitle()}
          </h1>
        </div>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 border border-[#DDE7D8] rounded-xl bg-[#FAFBF8] text-[#2F5E2E] active:scale-95 transition"
        >
          {isSidebarOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>
      </div>

      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-x-0 bottom-0 top-16 bg-black/40 z-20 transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div
        className={`
    fixed md:sticky top-16 md:top-4 left-0 z-30 md:z-auto
    w-[280px]
    bg-white
    p-4
    shadow-md
    border border-[#DDE7D8]
    rounded-xl
    transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
    md:translate-x-0
    transition-transform duration-300 ease-in-out
    max-h-[calc(100vh-2rem)]
    overflow-y-auto
  `}
      >
        <div className="md:hidden flex justify-between items-center pb-2 border-b border-gray-100">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            Navigation Menu
          </span>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="text-gray-400 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarContent />
      </div>

      <div className="flex-1 p-4 md:p-6 w-full">{renderContent()}</div>
    </div>
  );
};

export default CustomerProfileScreen;
